import { useState } from 'react';
import {
  OUTREACH_PICK_COUNT_MAX,
  REACH_CHANNELS,
  REACH_CHANNEL_LABELS,
  type ReachChannel,
} from '@aegislab/shared';
import { Button, ConfirmDialog, Input } from '@/shared/ui/atoms';
import * as s from './CandidateSelectionBar.css';

export interface CandidateSelectionBarProps {
  /** 이번에 닿을 매체. 이메일은 인원을 지정해 보내고, 다른 매체는 이메일 없는 사람을 목록으로 본다. */
  reachChannel: ReachChannel;
  onReachChannelChange: (channel: ReachChannel) => void;
  mode: 'PICKED' | 'ALL_MATCHING';
  /** 조건에 맞는 인원. 조회가 끝나기 전엔 `null` 이다 — 0 으로 적지 않는다. */
  totalCount: number | null;
  /** 고른 사람 모드에서 초안에 든 수. 페이지가 아니라 **초안 전체**다. */
  selectedCount: number;
  /** 지금 표 한 페이지에 보이는 인원. */
  pageCount: number;
  /** 초안에서 실제로 보낼 수 있는 계정 수. 묶음을 받기 전엔 `null` — 고른 수를 보낼 수 있는 수로 읽히지 않게 한다. */
  sendableCount?: number | null;
  /** 고른 사람 가운데 주소 없음 등으로 발송에서 빠지는 계정 수. */
  dropCount?: number | null;
  /**
   * 조건 전체 가운데 주소가 있는 수 — 크롤러가 가끔 세어 준 값이다. 없으면 `null`.
   * 조회마다 세지 않으므로 반드시 기준 시각과 함께 보인다.
   */
  addressTotal?: { count: number; asOf: string } | null;
  onPickPage: () => void;
  /** 이번에 보낼 인원을 정한다. 같은 조회에서 아직 안 보낸 사람을 순서대로 고른다. */
  onPickCount: (count: number) => void;
  pickPending?: boolean;
  pickError?: string | null;
  /** 리전이 비어 조회에서 빠진 인원. **조건 리전과 무관한 전역 수**다. */
  nullRegionCount?: number | null;
  snapshotAt?: string | null;
  expiresAt?: string | null;
}

const numberFormat = new Intl.NumberFormat('ko-KR');

function formatDay(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

/**
 * 선택 줄 — 승인 시안 CandidatesAfter 위에 퀘스트 2f546c56 B안.
 *
 * 기본은 아무도 안 고른 상태이고, 담당자가 이번에 보낼 인원을 직접 적는다(IA §3-3 「선택」).
 * 조건 전체는 확인창을 거쳐 고르는 선택이다. 예전에는 배너 한 줄이 두
 * 방향 전환의 유일한 길이었는데, 그 배너를 걷고 **두 칸 토글**로 옮겼다. 두 칸을 한 버튼으로 합치면
 * 담당자가 2만 명을 고르려다 한 페이지만 고르거나 그 반대가 된다 — 그래서 지금 모드는 눌린
 * 칸(`aria-pressed`)으로 말하고, 눌린 칸을 다시 눌러도 아무 일이 없다.
 *
 * 조건에 맞는 인원은 **이 줄에만** 적는다. 리전 미상 수는 그 옆에 낮은 무게로 둔다 — 나란히
 * 굵게 두면 "이 조회에서 빠진 그 나라 사람" 으로 읽힌다.
 */
export function CandidateSelectionBar({
  reachChannel,
  onReachChannelChange,
  mode,
  totalCount,
  selectedCount,
  pageCount,
  sendableCount = null,
  dropCount = null,
  addressTotal = null,
  onPickPage,
  onPickCount,
  pickPending = false,
  pickError = null,
  nullRegionCount = null,
  snapshotAt = null,
  expiresAt = null,
}: CandidateSelectionBarProps) {
  const [draft, setDraft] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const wanted = /^\d+$/.test(draft) ? Number(draft) : null;
  const wantedValid = wanted !== null && wanted > 0 && wanted <= OUTREACH_PICK_COUNT_MAX;
  const total = totalCount === null ? '?' : numberFormat.format(totalCount);
  const all = mode === 'ALL_MATCHING';
  const snapshotDay = formatDay(snapshotAt);
  const expiresDay = formatDay(expiresAt);
  const sendableLine =
    sendableCount === null ? null : (
      <span className={s.meta}>
        보낼 수 있는 <strong className={s.strong}>{numberFormat.format(sendableCount)}명</strong>
        {dropCount !== null && dropCount > 0
          ? ` · 주소 없음 등으로 ${numberFormat.format(dropCount)}명은 빠짐`
          : ''}
        {` · 조건 전체 ${total}명`}
        {addressTotal !== null
          ? ` 중 주소 있음 ${numberFormat.format(addressTotal.count)}명 (${formatDay(addressTotal.asOf) ?? '?'} 기준)`
          : ' · 주소 보유 총량은 아직 집계되지 않음'}
      </span>
    );
  const selectionLine = sendableLine ?? (
    <span className={s.meta}>
      조건에 맞는 <strong className={s.strong}>{total}명</strong> 중 직접 고른{' '}
      <strong className={s.strong}>{numberFormat.format(selectedCount)}명</strong>
    </span>
  );
  return (
    <div className={s.bar}>
      <div className={s.toggle} role="group" aria-label="섭외 방법">
        {REACH_CHANNELS.map((channel) => (
          <Button
            key={channel}
            variant="chip"
            size="xs"
            aria-pressed={reachChannel === channel}
            onClick={() => onReachChannelChange(channel)}
          >
            {REACH_CHANNEL_LABELS[channel]}
          </Button>
        ))}
      </div>
      {reachChannel === 'OTHER_MEDIA' ? (
        <span className={s.meta}>
          조건에 맞는 <strong className={s.strong}>{total}명</strong> 중 이메일이 없는 사람입니다.
          인스타그램 등 다른 매체는 자동 발송되지 않아 직접 섭외합니다
        </span>
      ) : all ? (
        <>
          <div className={s.toggle} role="group" aria-label="담을 대상">
            <Button variant="chip" size="xs" aria-pressed disabled>
              {sendableLine === null ? (
                <>
                  조건 전체 <strong className={s.strong}>{total}명</strong>
                </>
              ) : (
                '조건 전체 담음'
              )}
            </Button>
            <Button
              variant="chip"
              size="xs"
              aria-pressed={false}
              disabled={pageCount === 0}
              onClick={onPickPage}
            >
              {`이 페이지만 ${numberFormat.format(pageCount)}명`}
            </Button>
          </div>
          {sendableLine}
        </>
      ) : (
        <>
          <Button
            variant="chip"
            size="xs"
            disabled={totalCount === null || pickPending}
            onClick={() => setConfirmAll(true)}
          >
            조건 전체에서 {numberFormat.format(OUTREACH_PICK_COUNT_MAX)}명씩
          </Button>
          {selectionLine}
        </>
      )}
      {reachChannel === 'EMAIL' ? (
        <>
          <form
            className={s.pick}
            onSubmit={(event) => {
              event.preventDefault();
              if (wantedValid && wanted !== null) onPickCount(wanted);
            }}
          >
            <Input
              className={s.pickInput}
              inputSize="xs"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              aria-label="이번에 보낼 인원"
              placeholder="이번에 보낼 인원"
              value={draft}
              onChange={(event) => setDraft(event.target.value.trim())}
            />
            <Button variant="chip" size="xs" type="submit" disabled={!wantedValid || pickPending}>
              인원 지정
            </Button>
          </form>
          <span className={s.meta}>
            이메일 섭외는 자동으로 발송됩니다. 이메일이 있는 사람만 고르고, 없는 사람은 「다른
            매체」에서 직접 섭외해 주세요.
          </span>
        </>
      ) : null}
      {reachChannel === 'EMAIL' && pickError !== null ? (
        <span className={s.meta} role="alert">
          {pickError}
        </span>
      ) : null}
      {nullRegionCount !== null && nullRegionCount > 0 ? (
        <span className={s.meta} title="리전이 비어 있는 사람은 어느 나라 조회에도 뜨지 않습니다.">
          리전 미상 {numberFormat.format(nullRegionCount)}명은 빠집니다
        </span>
      ) : null}
      {snapshotDay !== null ? (
        <span
          className={s.snapshot}
          title={expiresDay === null ? undefined : `${expiresDay}까지 이 조회 결과를 씁니다.`}
        >
          {snapshotDay} 조회
        </span>
      ) : null}
      <ConfirmDialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        onConfirm={() => {
          setConfirmAll(false);
          onPickCount(OUTREACH_PICK_COUNT_MAX);
        }}
        title="조건 전체에서 이번 회차를 고릅니다"
        variant="warning"
        confirmLabel="1만 명 선택"
        message={
          <p>
            조건에 맞는 {total}명 중 이번 회차에는 이메일이 있는{' '}
            {numberFormat.format(OUTREACH_PICK_COUNT_MAX)}명만 대상이 됩니다. 나머지는 같은 조회에서
            다음 회차에 이어 보낼 수 있습니다. 보통 50명을 섭외하려고 1만 명쯤 보내므로 큰
            규모입니다. 전부 보내면 다른 팀 캠페인의 후보가 소진되고 수신거부가 쌓입니다.
          </p>
        }
      />
    </div>
  );
}
